import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { AUTH_TOKEN, IS_MOBILE } from '../config';
import { audioEngine } from '../services/audioEngine';
import useMeetSession from '../hooks/useMeetSession';

// MeetDock — a live MEET inside the world: the artist (running the full web
// studio collab tab, X2 avatar and all) appears on the room's video panel;
// you stand in their studio and talk.
//
// Deep-link only for now: arriving with ?meet=<CODE> opens this dock with the
// code ready — App.jsx has already walked you into the room. Join is a button
// because getUserMedia and audio playback both want a user gesture, and
// because nobody should land in a live call without pressing something.
//
// Same staging trick as LiveChatDock: the remote stream lands in a hidden DOM
// <video>, worldStore.liveVideoEl hands it to the room's VideoPanel, and the
// panel — dormant until now, it has no recorded cameo — comes alive on the
// wall. Audio comes straight from the hidden element; the room's sound bed
// ducks to silence while the conversation owns the mix.

const VIDEO_EL_ID = 'world-meet-video';

const chip = {
  position: 'fixed',
  bottom: IS_MOBILE ? 'calc(max(18px, env(safe-area-inset-bottom)) + 110px)' : 18,
  left: '50%', transform: 'translateX(-50%)',
  display: 'flex', alignItems: 'center', gap: 10, zIndex: 30,
  background: 'rgba(10,8,12,0.78)', border: '1px solid #ffb25e', borderRadius: 999,
  padding: '10px 16px', color: '#ffd27d', font: '14px system-ui, sans-serif',
  backdropFilter: 'blur(6px)', maxWidth: '86vw',
};
const btn = {
  background: '#ffb25e', color: '#1c1206', border: 'none', borderRadius: 999,
  padding: '7px 14px', font: '600 13px system-ui, sans-serif', cursor: 'pointer',
};
const ghostBtn = { ...btn, background: 'transparent', color: '#ffd27d', border: '1px solid #ffb25e' };

// Your own camera, small and honest — you're on it, so you can see it.
const pip = {
  position: 'fixed', right: 14,
  bottom: IS_MOBILE ? 'calc(max(14px, env(safe-area-inset-bottom)) + 110px)' : 14,
  width: 120, height: 90, objectFit: 'cover', borderRadius: 10, zIndex: 30,
  border: '1px solid rgba(255,178,94,0.5)', transform: 'scaleX(-1)',
  background: '#0b0b12',
};

export default function MeetDock() {
  // The code rides the URL (?meet=ABCDE) — read once; the dock exists only
  // when a meet link brought you here.
  const meetCode = useMemo(() => {
    const c = new URLSearchParams(window.location.search).get('meet');
    return c ? c.trim().toUpperCase() : null;
  }, []);

  const zoom = useWorldStore((s) => s.zoom);
  const interior = useWorldStore((s) => s.interior);
  const setLiveVideoEl = useWorldStore((s) => s.setLiveVideoEl);
  const {
    phase, live, active, error, mediaNote, peer,
    localStream, remoteStream, micOn, camOn,
    join, leave, toggleMic, toggleCam,
  } = useMeetSession();

  // Remote stream → hidden video → the room's panel. The join click was the
  // user gesture, so unmuted playback is allowed; if a browser still refuses,
  // retry muted rather than showing a frozen panel.
  const remoteRef = useRef(null);
  useEffect(() => {
    const el = remoteRef.current;
    if (!el || !remoteStream) return undefined;
    el.srcObject = remoteStream;
    el.play().catch(() => { el.muted = true; el.play().catch(() => {}); });
    setLiveVideoEl(el);
    audioEngine.duck(true); // conversation leads the mix; the city stays under it
    return () => {
      setLiveVideoEl(null);
      audioEngine.duck(false);
      el.srcObject = null;
    };
  }, [remoteStream, setLiveVideoEl]);

  // Self-view PIP.
  const pipRef = useRef(null);
  useEffect(() => {
    const el = pipRef.current;
    if (!el || !localStream) return undefined;
    el.srcObject = localStream;
    el.play().catch(() => {});
    return () => { el.srcObject = null; };
  }, [localStream]);

  // Leaving the world page mid-meet: the hook's unmount teardown stops camera
  // and socket; nothing lingers.

  if (!meetCode) return null;
  // The dock waits until you're actually standing in the room (App.jsx walks
  // you in); showing join UI over the loading street invites a join with no
  // panel to land on.
  if (zoom !== ZOOM.INTERIOR || !interior) return null;

  if (!AUTH_TOKEN) {
    return (
      <div style={chip}>
        <span style={{ opacity: 0.9 }}>Meets are for members — log in on hypetunes.com, then reopen your meet link.</span>
      </div>
    );
  }

  const artistName = peer?.appearingAs || peer?.name || 'the artist';

  return (
    <>
      {/* Offscreen but PLAYING (not display:none, which some browsers
          throttle). The room's audio comes straight from this element. */}
      <video
        id={VIDEO_EL_ID}
        ref={remoteRef}
        playsInline
        autoPlay
        style={{ position: 'fixed', right: 0, bottom: 0, width: 2, height: 2, opacity: 0.01, pointerEvents: 'none' }}
      />

      {phase === 'idle' && (
        <div style={chip}>
          <span style={{ opacity: 0.9 }}>You're invited · code <b style={{ letterSpacing: 1 }}>{meetCode}</b></span>
          <button style={btn} onClick={() => join(meetCode)}>Join the meet</button>
        </div>
      )}

      {phase === 'starting' && (
        <div style={chip}><span>Setting up…</span></div>
      )}

      {phase === 'waiting' && (
        <div style={chip}>
          <span style={{ opacity: 0.9 }}>You're in — waiting for the artist to arrive…</span>
          <button style={ghostBtn} onClick={leave}>Leave</button>
        </div>
      )}

      {phase === 'connecting' && (
        <div style={chip}>
          <span>Connecting to {artistName}…</span>
          <button style={ghostBtn} onClick={leave}>Leave</button>
        </div>
      )}

      {live && (
        <div style={{ ...chip, borderColor: '#ff5e78' }}>
          <span style={{ color: '#ff5e78', fontWeight: 700 }}>● LIVE</span>
          <span style={{ opacity: 0.9 }}>{artistName} is on the studio screen</span>
          <button style={ghostBtn} onClick={toggleMic}>{micOn ? 'Mute' : 'Unmute'}</button>
          <button style={ghostBtn} onClick={toggleCam}>{camOn ? 'Cam off' : 'Cam on'}</button>
          <button style={{ ...ghostBtn, borderColor: '#ff5e78', color: '#ff9eb0' }} onClick={leave}>Leave</button>
        </div>
      )}

      {(phase === 'ended' || phase === 'error') && (
        <div style={chip}>
          <span style={{ opacity: 0.9 }}>{phase === 'ended' ? 'The meet has ended.' : (error || 'Something went wrong.')}</span>
          {phase === 'error' && <button style={btn} onClick={() => join(meetCode)}>Try again</button>}
        </div>
      )}

      {mediaNote && active && (
        <div style={{ ...chip, bottom: undefined, top: 74, fontSize: 12.5, opacity: 0.9 }}>
          <span>{mediaNote}</span>
        </div>
      )}

      {/* Self view only while your camera is part of the meet. */}
      {active && localStream && camOn && (
        <video ref={pipRef} playsInline autoPlay muted style={pip} />
      )}
    </>
  );
}
